import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { AccountingEntityType } from '@numeo/types-main-web-app'
import { Eye, EyeOff } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ds2'
import { useAccountingReadStatus } from '@/pages/dashboard/pages/accounting/pages/invoices/hooks/useAccountingReadStatus'
import { formatCount, formatUsd } from '../results/format'
import { emailBlockOf, invoiceBlockOf, statusOf } from './invoicingRows'
import type { BatchMode } from './CreateBatchDrawer'

/** The Invoices page, where Close Invoice(s) and Delete stay. */
const INVOICES_PAGE = '/accounting/invoices'

const INVOICED = new Set(['draft', 'sent', 'batch_prepared'])

interface SelectionBarProps {
    rows: TmsInvoiceRow[]
    /** The selected loads' amounts summed: the invoice total where the load has one, else its line haul. */
    total: number
    factoringConnected: boolean
    onClear: () => void
    /** Opens the one load's review drawer, where its invoice is created. */
    onCreateInvoice: (row: TmsInvoiceRow) => void
    onBatch: (mode: BatchMode) => void
    onSend: () => void
}

/**
 * The bar under the table while loads are selected, with the Invoices page's Ready to Invoice actions: Create invoice (one load) or
 * Create batch (several), Add to batch, the send the factoring connection allows, and Mark read. Close and Delete stay on the page.
 */
export const SelectionBar = ({ rows, total, factoringConnected, onClear, onCreateInvoice, onBatch, onSend }: SelectionBarProps) => {
    const loadIds = rows.map((row) => row.load_id).filter((id): id is string => Boolean(id))
    const reads = useAccountingReadStatus(loadIds, AccountingEntityType.LOAD, { holdUnknown: true })
    const allRead = loadIds.length > 0 && loadIds.every((id) => reads.isRead(id))

    // The page's rule: loads are batched only while none of them is invoiced and none is billed through Amazon.
    const uninvoiced = rows.every((row) => !INVOICED.has(statusOf(row)))
    const amazon = rows.some((row) => row.billed_through_amazon)
    const canBatch = uninvoiced && !amazon
    const sendable = rows.every((row) => (factoringConnected ? invoiceBlockOf(row) : emailBlockOf(row)) === null)
    const sendLabel = factoringConnected ? 'Submit to factoring' : 'Send by email'
    const hint = amazon
        ? 'Amazon-billed loads are invoiced through Amazon.'
        : !canBatch && !sendable
          ? 'Select loads that are not invoiced yet to batch them, or invoices to send.'
          : null

    return (
        <div className="flex flex-col gap-2 rounded-xl border border-accent-soft bg-accent-soft px-3 py-2.5 text-xs text-accent-soft-foreground">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                    <strong className="font-semibold">{formatCount(rows.length)} selected</strong> · {formatUsd(total)}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={onClear}>
                        Clear
                    </Button>
                    {loadIds.length > 0 && (
                        <Button
                            size="sm"
                            variant="ghost"
                            prefix={allRead ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : <Eye className="h-3.5 w-3.5" aria-hidden />}
                            onClick={() => void reads.bulkToggleRead(loadIds, !allRead)}
                        >
                            {allRead ? 'Mark unread' : 'Mark read'}
                        </Button>
                    )}
                    {canBatch && (
                        <Button size="sm" variant="secondary" onClick={() => onBatch('existing')}>
                            Add to batch
                        </Button>
                    )}
                    {sendable && (
                        <Button size="sm" variant={canBatch ? 'secondary' : 'primary'} onClick={onSend}>
                            {sendLabel}
                        </Button>
                    )}
                    {canBatch &&
                        (rows.length === 1 ? (
                            <Button size="sm" onClick={() => onCreateInvoice(rows[0])}>
                                Create invoice
                            </Button>
                        ) : (
                            <Button size="sm" onClick={() => onBatch('new')}>
                                Create batch
                            </Button>
                        ))}
                </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-foreground-muted">
                <span>{hint ?? ''}</span>
                <Link to={INVOICES_PAGE} className="text-accent-soft-foreground underline">
                    Close or delete on the Invoices page →
                </Link>
            </div>
        </div>
    )
}
